import { Link } from 'react-router-dom';
import { Clock, DoorOpen, Monitor, UserCheck } from 'lucide-react';
import type { Dashboard } from 'shared';
import { formatCount, formatDuration, formatShare } from '@/lib/reportFormat';

interface TileProps {
  title: string;
  value: string;
  icon: React.ElementType;
  children?: React.ReactNode;
}

function Tile({ title, value, icon: Icon, children }: TileProps) {
  return (
    <div className="card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="mt-1 text-3xl font-bold text-foreground tabular-nums">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-primary/10 text-primary">
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
      </div>
      {children}
    </div>
  );
}

/** How today's entries compare with yesterday's whole day. */
function againstYesterday(today: number, yesterday: number): string {
  if (yesterday === 0) return 'Ontem: nenhuma';
  return `Ontem: ${formatCount(yesterday)}${today > yesterday ? ', já ultrapassado' : ''}`;
}

/** The figures of today, at a glance. */
export default function DashboardTiles({ dashboard }: { dashboard: Dashboard }) {
  const { equipmentUnits: units, equipmentUnitsInUse: inUse } = dashboard;
  const occupied = units > 0 ? Math.min(100, (inUse / units) * 100) : 0;
  // The figure above counts what is out; the line under it, what is left.
  const free = Math.max(0, units - inUse);

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
      <Tile title="No centro agora" value={formatCount(dashboard.presentNow)} icon={UserCheck}>
        <Link to="/presencas" className="text-xs font-medium text-primary hover:underline">
          Ver presenças
        </Link>
      </Tile>

      <Tile title="Entradas hoje" value={formatCount(dashboard.visitsToday)} icon={DoorOpen}>
        <p className="text-xs text-muted-foreground">
          {againstYesterday(dashboard.visitsToday, dashboard.visitsYesterday)}
        </p>
      </Tile>

      <Tile title="Equipamento em uso" value={`${formatCount(inUse)} / ${formatCount(units)}`} icon={Monitor}>
        <div>
          <div
            className="h-1.5 bg-muted rounded-full overflow-hidden"
            role="meter"
            aria-label="Ocupação do equipamento"
            aria-valuemin={0}
            aria-valuemax={units}
            aria-valuenow={Math.min(inUse, units)}
          >
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${occupied}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {units === 0 ? 'Sem equipamento disponível' : `${formatShare(free, units)} disponíveis`}
          </p>
        </div>
      </Tile>

      <Tile title="Horas de equipamento hoje" value={formatDuration(dashboard.equipmentHoursToday)} icon={Clock}>
        <p className="text-xs text-muted-foreground">
          Esta semana:{' '}
          <span className="font-semibold text-foreground">{formatDuration(dashboard.equipmentHoursWeek)}</span>
        </p>
      </Tile>
    </div>
  );
}
